import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable, of, switchMap, timer } from 'rxjs';
import packageJson from '../../../../package.json';
import bundledAppVersionJson from '../../../assets/app-version.json';

export interface AppVersionInfo {
  version: string;
  buildId?: string;
  releasedAt?: string;
}

const bundledAppVersion = bundledAppVersionJson as AppVersionInfo;

/**
 * The displayed version is the semver from `package.json`, bumped through the `version-*` npm
 * scripts. `buildId` is deliberately kept out of it — it exists to detect new deploys in
 * `isUpdateAvailable()`, not to identify the release to users.
 */
export function formatAppReleaseLabel(
  info: AppVersionInfo,
  fallbackVersion: string,
): string {
  return info.version?.trim() || fallbackVersion;
}

@Injectable({
  providedIn: 'root',
})
export class VersionService {
  private readonly http = inject(HttpClient);
  private readonly currentVersion = packageJson.version;
  private readonly bundledBuildId = bundledAppVersion.buildId;

  /** Human-readable release label baked in at build time. */
  getAppVersion(): string {
    return formatAppReleaseLabel(bundledAppVersion, this.currentVersion);
  }

  /** Fetches the deployed assets version (cache-busted). */
  private getDeployedVersion(): Observable<AppVersionInfo> {
    const url = `/assets/app-version.json?v=${Date.now()}`;
    return this.http.get<AppVersionInfo>(url, {
      headers: {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      },
    });
  }

  /** True when the hosted app-version.json buildId differs from this bundle. */
  isUpdateAvailable(): Observable<boolean> {
    const bundledBuildId = this.bundledBuildId;
    if (!bundledBuildId) {
      return of(false);
    }

    return this.getDeployedVersion().pipe(
      map((deployed) => !!deployed.buildId && deployed.buildId !== bundledBuildId),
      catchError(() => of(false)),
    );
  }

  checkVersionPeriodically(intervalMs = 60000): Observable<boolean> {
    return timer(0, intervalMs).pipe(switchMap(() => this.isUpdateAvailable()));
  }
}
